import type { DocumentProviderError } from "@cloudox/api-client";

interface Props {
  readonly error: DocumentProviderError;
  readonly onRetry?: () => void;
}

export function ErrorState({ error, onRetry }: Props) {
  const { title, description } = describe(error);
  return (
    <div className="mx-auto flex max-w-md flex-col items-center text-center" style={{ color: "var(--fg-muted)" }}>
      <div
        className="mb-4 flex h-12 w-12 items-center justify-center rounded-full"
        style={{ backgroundColor: "color-mix(in oklab, var(--danger) 12%, transparent)" }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden style={{ color: "var(--danger)" }}>
          <circle cx="12" cy="12" r="10" />
          <path d="M12 8v4M12 16h.01" />
        </svg>
      </div>
      <h2 className="text-lg font-semibold" style={{ color: "var(--fg)" }}>
        {title}
      </h2>
      <p className="mt-1 text-sm">{description}</p>
      {onRetry ? (
        <button type="button" onClick={onRetry} className="btn btn-secondary mt-4">
          Try again
        </button>
      ) : null}
    </div>
  );
}

function describe(error: DocumentProviderError): { title: string; description: string } {
  switch (error.code) {
    case "not_found":
      return {
        title: "Document not found",
        description: "The selected document is not in the current manifest. It may have been moved or removed.",
      };
    case "invalid_document":
      return {
        title: "Invalid document",
        description: "The document could not be parsed. The content source may be misconfigured.",
      };
    case "unauthorized":
      return {
        title: "Access denied",
        description: "You do not have permission to view this document. Authentication is not yet wired up in this preview.",
      };
    case "network_error":
      return {
        title: "Could not load",
        description: "We could not reach the content service. Check your connection and try again.",
      };
    case "unknown":
    default:
      return {
        title: "Something went wrong",
        description: error.message || "Unexpected error while loading the document.",
      };
  }
}
